// Printable BEAD Draw Packet — formatted to look like a packet a State
// Broadband Office reviewer would expect. Browser-print-to-PDF.

import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

const fmtUSD = (n: number) => `$${Math.round(n).toLocaleString()}`;

export default function ReimbursementPacketPage() {
  const { projectId } = useParams();
  const [search] = useSearchParams();
  const period_start = search.get('period_start') ?? '2026-04-01';
  const period_end   = search.get('period_end')   ?? '2026-04-30';
  const progress_pct = search.get('progress_pct') ?? '30';

  const { data: draw, isLoading } = useQuery<any>({
    queryKey: ['packet', projectId, period_start, period_end, progress_pct],
    queryFn: () => api.get(`/bead/projects/${projectId}/reimbursement?period_start=${period_start}&period_end=${period_end}&progress_pct=${progress_pct}`),
    enabled: !!projectId,
  });

  if (isLoading || !draw) return <div style={{ padding: 24 }}>Loading packet…</div>;

  const fed_pct = (draw.project.federal_share_pct * 100).toFixed(1);
  const drawNumber = '001';

  return (
    <div style={{
      maxWidth: 800, margin: '0 auto', padding: '40px 50px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: 12, color: '#0f172a', lineHeight: 1.5,
    }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff; }
          @page { margin: 0.5in; }
        }
        table { border-collapse: collapse; width: 100%; }
        th, td { padding: 8px 10px; text-align: left; }
        thead { background: #1e293b; color: #fff; }
        thead th { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; font-weight: 700; }
        tbody tr:nth-child(odd) { background: #f8fafc; }
      `}</style>

      <div className="no-print" style={{ marginBottom: 20, padding: 10, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, textAlign: 'center' }}>
        Use your browser's <b>Print → Save as PDF</b> to export this packet.
        <button onClick={() => window.print()} style={{ marginLeft: 12, padding: '4px 12px', background: '#0f172a', color: '#fff', border: 0, borderRadius: 3, cursor: 'pointer' }}>Print</button>
      </div>

      {/* Cover */}
      <div style={{ borderBottom: '3px solid #0f172a', paddingBottom: 14, marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em' }}>BEAD Subgrantee Reimbursement Request</div>
        <h1 style={{ fontSize: 26, margin: '6px 0 0 0', fontWeight: 800 }}>Draw Packet #{drawNumber}</h1>
        <div style={{ marginTop: 8, color: '#475569', fontSize: 13 }}>
          Period: {period_start} – {period_end}
        </div>
      </div>

      <table style={{ marginBottom: 20 }}>
        <tbody>
          <tr>
            <td style={{ width: '30%', color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Project Code</td>
            <td>{draw.project.project_code}</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Project Name</td>
            <td>{draw.project.project_name}</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>State / County</td>
            <td>{draw.project.state} · {draw.project.county} County</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Technology</td>
            <td>{draw.project.tech_type === 'fiber' ? 'Fiber to the Premises (FTTP)' : draw.project.tech_type}</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Awarded BSLs</td>
            <td>{Number(draw.project.awarded_bsl_count).toLocaleString()}</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Total Project Cost</td>
            <td>{fmtUSD(draw.project.total_project_cost_usd)}</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Federal Award (BEAD)</td>
            <td>{fmtUSD(draw.project.grant_amount_usd)} ({fed_pct}%)</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Subgrantee Match</td>
            <td>{fmtUSD(draw.project.match_amount_usd)} ({(100 - Number(fed_pct)).toFixed(1)}%)</td>
          </tr>
        </tbody>
      </table>

      {/* Summary box */}
      <div style={{ background: '#0f172a', color: '#fff', padding: 16, borderRadius: 4, marginBottom: 24 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: '#94a3b8', marginBottom: 8 }}>This Draw Period</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: '#cbd5e1' }}>Period actuals</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{fmtUSD(draw.totals.actual)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#86efac' }}>Federal share claimed</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>{fmtUSD(draw.totals.claimed_federal)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#fcd34d' }}>Match drawn</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#fbbf24' }}>{fmtUSD(draw.totals.drawn_match)}</div>
          </div>
        </div>
      </div>

      <h2 style={{ fontSize: 14, marginTop: 8, marginBottom: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#334155', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>
        Cost Reconciliation by Category
      </h2>

      <table style={{ marginBottom: 20 }}>
        <thead>
          <tr>
            <th>Category</th>
            <th style={{ textAlign: 'right' }}>Budget</th>
            <th style={{ textAlign: 'right' }}>Actual</th>
            <th style={{ textAlign: 'right' }}>Federal claim</th>
            <th style={{ textAlign: 'right' }}>Match</th>
            <th style={{ textAlign: 'center' }}>Var %</th>
          </tr>
        </thead>
        <tbody>
          {draw.categories.map((c: any) => (
            <tr key={c.category}>
              <td style={{ fontWeight: 600 }}>{c.category}</td>
              <td style={{ textAlign: 'right' }}>{fmtUSD(c.budget)}</td>
              <td style={{ textAlign: 'right' }}>{fmtUSD(c.actual)}</td>
              <td style={{ textAlign: 'right', color: '#047857', fontWeight: 600 }}>{fmtUSD(c.claimed_federal)}</td>
              <td style={{ textAlign: 'right', color: '#b45309' }}>{fmtUSD(c.drawn_match)}</td>
              <td style={{ textAlign: 'center', color: c.variance_flag === 'over' ? '#b91c1c' : '#475569' }}>
                {c.variance_pct > 0 ? '+' : ''}{c.variance_pct}%
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: '2px solid #0f172a', fontWeight: 700, background: '#f1f5f9' }}>
            <td>TOTAL</td>
            <td style={{ textAlign: 'right' }}>{fmtUSD(draw.totals.budget)}</td>
            <td style={{ textAlign: 'right' }}>{fmtUSD(draw.totals.actual)}</td>
            <td style={{ textAlign: 'right', color: '#047857' }}>{fmtUSD(draw.totals.claimed_federal)}</td>
            <td style={{ textAlign: 'right', color: '#b45309' }}>{fmtUSD(draw.totals.drawn_match)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <h2 style={{ fontSize: 14, marginTop: 24, marginBottom: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#334155', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>
        Supporting Documentation
      </h2>
      <ul style={{ fontSize: 12, color: '#475569', paddingLeft: 20 }}>
        <li>Bill of Materials reconciliation (per-segment, per-part)</li>
        <li>Crew time logs and work-order labor allocation</li>
        <li>Subcontractor invoices (HDD, splicing, locating) — from QuickBooks Online</li>
        <li>Permit and ROW fee documentation</li>
        <li>Engineering vendor invoices</li>
        <li>Photo evidence with geotag for each completed plant segment</li>
        <li>BABA / Davis-Bacon / civil rights compliance attestations (signed)</li>
      </ul>
      <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
        All supporting documentation is available on request and indexed against the line items above. Generated automatically from project records in Milestone Build.
      </div>

      {/* Sign-off block */}
      <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid #e2e8f0' }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#334155', marginBottom: 16 }}>Subgrantee Certification</h2>
        <p style={{ fontSize: 11, color: '#475569', marginBottom: 28 }}>
          The undersigned certifies that the costs reported above were actually incurred during the period stated, are properly chargeable to the BEAD subgrant agreement, comply with applicable federal and state requirements (including BABA, Davis-Bacon, and civil rights provisions), and have not been previously reimbursed.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, marginTop: 30 }}>
          <div>
            <div style={{ borderBottom: '1px solid #0f172a', height: 32 }} />
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>Authorized Signature</div>
          </div>
          <div>
            <div style={{ borderBottom: '1px solid #0f172a', height: 32 }} />
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>Date</div>
          </div>
          <div>
            <div style={{ borderBottom: '1px solid #0f172a', height: 32 }} />
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>Printed Name &amp; Title</div>
          </div>
          <div>
            <div style={{ borderBottom: '1px solid #0f172a', height: 32 }} />
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>Subgrantee Organization</div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 40, fontSize: 9, color: '#94a3b8', textAlign: 'center', borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
        Generated by Milestone Build · {new Date().toLocaleString()} · Project ID: {draw.project.id}
      </div>
    </div>
  );
}
